import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { Users, ListChecks, Send, AlertTriangle, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const KPI = ({ label, value, icon: Icon, accent }) => (
  <div className="card-soft p-6" data-testid={`kpi-${label.toLowerCase().replace(/\s/g,"-")}`}>
    <div className="flex items-start justify-between">
      <div className="tiny-label">{label}</div>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent || "bg-turq-50 text-turq-700"}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="mt-4 font-display font-black text-4xl text-slate-900 tracking-tight">{value}</div>
  </div>
);

const StatusBadge = ({ s }) => {
  const map = {
    queued: "bg-slate-100 text-slate-700",
    processing: "bg-cyan-100 text-cyan-800",
    completed: "bg-emerald-100 text-emerald-800",
    partial: "bg-amber-100 text-amber-800",
    failed: "bg-red-100 text-red-800",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${map[s] || "bg-slate-100"}`}>{s}</span>;
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="tiny-label mb-2">Panel</div>
          <h1 className="font-display font-black text-4xl text-slate-900">Genel Bakış</h1>
        </div>
        <Link to="/admin/customers" className="text-sm font-semibold text-turq-700 hover:text-turq-800 flex items-center gap-1" data-testid="quick-customers">
          Müşteri yönetimine git <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        <KPI label="Müşteri" value={stats?.customers ?? "—"} icon={Users} />
        <KPI label="Toplam İş" value={stats?.jobs ?? "—"} icon={ListChecks} />
        <KPI label="Gönderilen" value={stats?.total_sent ?? "—"} icon={Send} accent="bg-emerald-50 text-emerald-700" />
        <KPI label="Hata" value={stats?.total_failed ?? "—"} icon={AlertTriangle} accent="bg-red-50 text-red-600" />
      </div>

      <div className="card-soft overflow-hidden">
        <div className="px-6 py-4 border-b border-turq-100 flex items-center justify-between">
          <div className="font-display font-bold text-slate-900">Son SMS İşleri</div>
          <Link to="/admin/jobs" className="tiny-label hover:text-turq-700" data-testid="goto-jobs">Tümü →</Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50/50">
            <tr className="text-left border-b border-turq-100">
              <th className="px-6 py-3 tiny-label">Tarih</th>
              <th className="px-6 py-3 tiny-label">Müşteri</th>
              <th className="px-6 py-3 tiny-label">Mod</th>
              <th className="px-6 py-3 tiny-label">Toplam</th>
              <th className="px-6 py-3 tiny-label">Başarı / Hata</th>
              <th className="px-6 py-3 tiny-label">Durum</th>
            </tr>
          </thead>
          <tbody>
            {(stats?.recent_jobs || []).length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400 text-sm">Henüz iş yok</td></tr>
            )}
            {(stats?.recent_jobs || []).map((j) => (
              <tr key={j.id} className="border-b border-slate-100 hover:bg-turq-50/40" data-testid={`recent-job-${j.id}`}>
                <td className="px-6 py-3 font-mono text-xs">{new Date(j.created_at).toLocaleString("tr-TR")}</td>
                <td className="px-6 py-3">{j.customer_id?.slice(0,8)}…</td>
                <td className="px-6 py-3"><span className="tiny-label">{j.mode}</span></td>
                <td className="px-6 py-3 font-mono">{j.total}</td>
                <td className="px-6 py-3 font-mono text-xs">
                  <span className="text-emerald-700">{j.success_count}</span>
                  <span className="text-slate-400"> / </span>
                  <span className="text-red-700">{j.fail_count}</span>
                </td>
                <td className="px-6 py-3"><StatusBadge s={j.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

